<template>
  <BaseCollapsibleBlock :title="title"
                        class="keyword-base-table-collapsible"
                        :isCollapsed="false"
                        :isStaticView="isStaticView"
  >
    <template #date-picker>
      <base-date-picker-button :availableDates="availableDates"
                               :mostRecentOnClear="true"
                               :setLastDate="true"
                               :removeCaret="true"
                               :bigDatePicker="true"
                               :customPickerState="{placement: 'bottom'}"
                               v-model="briefContent.selectedDate" />
      <base-multi-select
        class="keywords__column-selector"
        :options="columnOptions"
        title="Columns"
        v-model="enabledColumns"
      />
    </template>
    <template #header-additional>
      <base-block-chip
        class="keyword-block-chip-filter"
          v-for="chip in keywordChips"
          :key="chip.value"
          :label="chip.label"
          @remove="removeFilter(chip.value)"
      />
      <div v-if="keywordChips.length"
           class="keyword-block-chip keyword-block-chip_clear-all"
           @click.stop="removeFilter('clearAll')">
        <span class="keyword-block-chip__name">clear all</span>
      </div>
      <button v-if="optimizedContent" class="rescore-optimized" @click.prevent.stop="getKissScore">
        <base-material-design-icon icon="cached"/>
        rescore optimized
      </button>
    </template>
    <template v-if="isKeywordsPresent" #button>
      <div @click.stop="exportCSV()">
        <base-material-design-icon icon="download"/>
      </div>
    </template>
    <div class="keywords">
      <div class="common-table">
        <div v-if="isBaselineDisplay" class="common-table__row common-table__summary">
          <div
            v-for="column in keywordsTableColumnsConfig"
            :key="column.propName"
            class="common-table__average_cell"
            :class="[`keywords__${column.propName}`]"
          >
            <template v-if="column.propName !== 'serp_features'">
              {{ summaryHeaderData[column.propName] || '' }}
            </template>
            <serp-feature-icons v-else-if="summaryHeaderData.serp_features" :featureList="[summaryHeaderData.serp_features]"/>
          </div>
        </div>
        <div class="common-table__row common-table__header" :class="{ 'common-table__header_navbar-uncollapsed': !isNavbarCollapsed }">
          <div class="common-table__header-cell"
               v-for="column in keywordsTableColumnsConfig"
               :key="column.propName"
               :class="{
                'common-table__header-cell_sortable': column.isSortable,
                [`keywords__${column.propName}`]: true,
                'common-table__header-cell_zero-padding': ['keyword', 'serp_features', 'baseline_score', 'optimized_score'].includes(column.propName),
              }"
               @click.exact="onHeaderCellClicked(column)"
               @click.shift="onHeaderCellClickedWithShift(column)">
            <div class="common-table__header-cell__wrapper"
                 @click.stop="()=>{}"
                 v-if="['keyword', 'serp_features', 'baseline_score', 'optimized_score'].includes(column.propName)">
              <base-multi-select-sortable-header
                :ref="column.propName"
                :column="column"
                :sort-config="sortConfig"
                :predefinedNumberFilter="!['keyword', 'serp_features'].includes(column.property)
                  ? filterParams[column.property]
                  : null"
                @sort="onHeaderClicked(column, $event)"
                @apply="filterColumn($event, column)"
              />
            </div>
            <base-table-sortable-column
              v-else-if="column.isSortable"
              :label="column.label"
              :prop-name="column.propName"
              :sort-config="sortConfig"/>
            <template v-else>{{column.label}}</template>
          </div>
        </div>
        <div v-if="dataMissing" class="common-table__row no-data-block">
          No records found
        </div>
        <div v-else-if="isDataUpdating" class="common-table__row no-data-block">
          <base-horizontal-loader />
        </div>
        <div v-else class="common-table__row"
             v-for="(keyword, index) in sortedKeywords"
             :key="index">
          <div class="common-table__cell"
               v-for="(column, index) in keywordsTableColumnsConfig"
               :key="index"
               :class="`keywords__${column.propName}`">
            <template v-if="column.valueFilter">
              {{applyFilter(keyword[column.propName], column.valueFilter)}}
            </template>
            <template v-else-if="column.isSelectable">
              <base-select class="keywords__select" :options="scoreOptions"
                           :borderLess="true"
                           :showTopBorder="true"
                           :disabled="isCurrentUserClientCollaborator"
                           alignLeft
                           placeholder="Select Field"
                           :selectStyle="{height: '60px!important'}"
                           v-model="keyword[column.propName]"
                           @change="onRelScoreChanged(keyword)"/>
            </template>
            <template v-else-if="column.propName === 'diff_score'">
              <base-page-title
                :title="keyword[column.propName]"
                :disabled="isCurrentUserClientCollaborator"
                type="number"
                @change-slug-name="updateKeywordDiff($event, keyword)" />
            </template>
            <template v-else-if="column.isSerp">
              <serp-feature-icons :featureList="keyword.serp_features"/>
            </template>
            <template v-else-if="column.isLink">
              <template v-if="keyword[column.propName]">
                <a v-tooltip.top="{content: mappedRankingUrl(keyword[column.propName]), ...tooltipOptions}"
                   :href="keyword[column.propName]"
                   :class="{'url-mismatch': checkUrlsMismatch(keyword)}"
                   class="keywords__ranking_url__link"
                   target="_blank">
                  <base-truncate-label :shrink="1" :label="keyword[column.propName]" :watchClass="'keywords__ranking_url__link'"/>
                </a>
              </template>
              <p style="margin: 0" v-else :class="`keywords__${column.propName}`">NR</p>
            </template>
            <template v-else-if="column.propName === 'keyword'">
              <span class="keywords__ranking_url__keyword">
                <base-search-in-google class="keywords__ranking_url__keyword__search"
                                       :query="keyword[column.propName]" />
                <span class="keywords__ranking_url__keyword_keyword" @click="openModal(keyword[column.propName])">{{keyword[column.propName]}}</span>
              </span>
            </template>
            <span v-else-if="['change'].includes(column.propName)"
                  :style="{color: keyword.change > 0 ? '#149f56' : keyword.change < 0 ? '#ff4f4f' : '#999'}">
              {{getChangeValue(keyword[column.propName])}}
            </span>
            <template v-else-if="['optimized_score', 'baseline_score'].includes(column.propName)">
              <OptimizedScoreSummary
                :summary="getSummary(keyword, column.propName)"
              >
                {{ keyword[column.propName] }}
              </OptimizedScoreSummary>
            </template>
            <template v-else>
              {{keyword[column.propName]}}
            </template>
          </div>
        </div>
      </div>
      <modal name="history"
             v-bind="modalProps"
      >
        <base-keyword-history-modal :keyword="selectedKeyword" :comparison="comparisonArray" @close="closeModal"/>
      </modal>
    </div>

  </BaseCollapsibleBlock>
</template>

<script>
/* eslint-disable no-unused-vars */

import { mapState, mapActions } from 'vuex'

import filters from '@/common/Filters'
import csvExport from '@/services/CSVExport'
import moment from 'moment'
import helper from '@/services/Helper'
import BaseSortMixins from '@/common/BaseSortMixins'
import SerpFeatureIcons from './SerpFeatureIcons'
import baseModalProps from '@/common/BaseModalProps'
import _ from 'lodash'
import OptimizedScoreSummary from '@/features/content-briefs/brief-content/OptimizedScoreSummary'
import { EventBus } from '@/services/eventBus'

const BASELINE_COLUMNS = ['baseline_score', 'optimized_score', 'change']
const FILTER_PARAMS = {
  keyword: '',
  baseline_score: {
    operator: null,
    value: null
  },
  optimized_score: {
    operator: null,
    value: null
  },
  serp_features: []
}
const DEFAULT_CONFIG = [
  {
    label: 'Keyword Phrase',
    propName: 'keyword',
    sortByLenght: false,
    // this value used in BaseMultiSelectSortableHeader
    property: 'keyword'
  },
  {
    label: 'Baseline<br/>KISS',
    propName: 'baseline_score',
    property: 'baseline_score',
    isNumber: true,
    isClearOnCross: true,
    isSmartInput: false
  },
  {
    label: 'Optimized<br/>KISS',
    propName: 'optimized_score',
    property: 'optimized_score',
    isNumber: true,
    isClearOnCross: true,
    isSmartInput: false
  },
  {
    label: 'KISS<br/>Change',
    property: 'change',
    propName: 'change',
    isSortable: true
  },
  {
    label: 'MSV',
    propName: 'monthly_search_volume',
    isSortable: true,
    sortByLenght: false,
    valueFilter: 'commaDividedNumber',
    sortPreFormatting: function (value) {
      const clearedValue = value.replace(',', '')
      return Number(clearedValue)
    }
  },
  {
    label: 'Rel Score',
    propName: 'score',
    isSelectable: true,
    sortByLenght: false,
    isSortable: true
  },
  {
    label: 'Diff Score',
    propName: 'diff_score',
    isSelectable: false,
    sortByLenght: false,
    isSortable: true
  },
  {
    label: 'SERP feature',
    propName: 'serp_features',
    // this value used in BaseMultiSelectSortableHeader
    property: 'serp_features',
    sortByLenght: true,
    isSerp: true,
    isSmartInput: false,
    options: [
      {
        label: 'Answer Box',
        value: 'answer_box'
      },
      {
        label: 'Brand Pack',
        value: 'brand_pack'
      },
      {
        label: 'Image Pack',
        value: 'image_pack'
      },
      {
        label: 'Knowledge Graph',
        value: 'knowledge_graph'
      },
      {
        label: 'Local 3 Pack',
        value: 'local_3_pack'
      },
      {
        label: 'News Pack',
        value: 'news_pack'
      },
      {
        label: 'People Also Ask',
        value: 'people_also_ask'
      },
      {
        label: 'Related Searches',
        value: 'related_searches'
      },
      {
        label: 'Shopping Box',
        value: 'shopping_box'
      },
      {
        label: 'Video Pack',
        value: 'video_pack'
      },
      {
        label: 'Ad Count',
        value: 'ad_count'
      },
      {
        label: 'Geo',
        value: 'geo'
      },
      {
        label: 'Locale',
        value: 'locale'
      },
      {
        label: 'Total Results',
        value: 'total_number_of_results'
      },
      {
        label: 'Empty',
        value: 'empty'
      }
    ]
  },
  {
    label: 'Rank Zone',
    propName: 'rank_zone',
    isSortable: true,
    sortByLenght: false
  },
  {
    label: 'Ranked URL',
    propName: 'ranking_url',
    isSortable: true,
    sortByLenght: false,
    isLink: true
  }
]
const DEFAULT_SELECTED_COLUMNS = ['keyword', 'monthly_search_volume', 'score', 'diff_score', 'rank_zone', 'ranking_url']
const DEFAULT_SELECTED_COLUMNS_CREATION = ['keyword', 'monthly_search_volume', 'score', 'diff_score', 'rank_zone', 'ranking_url', 'baseline_score', 'optimized_score', 'change']

export default {
  name: 'Keywords',
  components: { SerpFeatureIcons, OptimizedScoreSummary },
  mixins: [BaseSortMixins],
  created () {
    try {
      const enabledColumns = JSON.parse(localStorage.getItem(this.isBaselineDisplay ? 'enabledColumnsKeywordCreation' : 'enabledColumnsKeyword'))
      if (Array.isArray(enabledColumns)) {
        this.enabledColumns = enabledColumns
      } else {
        this.enabledColumns = [...(this.isBaselineDisplay ? DEFAULT_SELECTED_COLUMNS_CREATION : DEFAULT_SELECTED_COLUMNS)]
      }
    } catch {
      this.enabledColumns = [...(this.isBaselineDisplay ? DEFAULT_SELECTED_COLUMNS_CREATION : DEFAULT_SELECTED_COLUMNS)]
    }

    EventBus.$on('keywords-added', this.handleKeywordsAdded)
  },
  beforeDestroy() {
    EventBus.$off('keywords-added', this.handleKeywordsAdded)
  },
  props: {
    keywords: {
      type: Array
    },
    isStaticView: {
      type: Boolean,
      default: false
    },
    isBaselineDisplay: {
      type: Boolean,
      default: false
    },
    isKeywordsPresent: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Keywords'
    },
    optimizedContent: {
      type: String,
      default: ''
    },
    cbVersionId: {
      type: String,
      default: ''
    },
    webPropertyGroupId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      sortConfig: [{
        sortDirection: helper.DIRECTION_ASC,
        propertyName: 'rank_zone'
      }],
      scoreOptions: [
        {
          label: -1,
          value: -1
        },
        {
          label: 0,
          value: 0
        }, {
          label: 1,
          value: 1
        }, {
          label: 2,
          value: 2
        }, {
          label: 3,
          value: 3
        }, {
          label: 4,
          value: 4
        }, {
          label: 5,
          value: 5
        }],
      exportButton: true,
      keywordChips: [],
      isDataUpdating: false,
      enabledColumns: [],
      filterParams: _.cloneDeep(FILTER_PARAMS),
      tooltipOptions: {
        delay: { show: 100, hide: 1300 },
        classes: 'rfx-tooltip-arrow',
        trigger: 'hover'
      },
      // selected keywords for keyword History Modal
      selectedKeyword: ''
    }
  },
  watch: {
    'briefContent.selectedDate': {
      async handler (date, prevDate) {
        if (date === prevDate) return
        await this.updateKeywords(date)
      },
      immediate: true
    },
    enabledColumns: {
      handler (v) {
        localStorage.setItem(this.isBaselineDisplay ? 'enabledColumnsKeywordCreation' : 'enabledColumnsKeyword', JSON.stringify(v))
      },
      deep: true
    },
    async webPropertyGroupId (data, prevData) {
      if (!data || data === prevData) return

      this.isDataUpdating = true
      await this.getKeywordsSnapshot({
        clientId: this.clientId,
        briefId: this.briefContent._id,
        web_property_group_id: data,
        snapshotDate: moment(this.briefContent.selectedDate).format('YYYY-MM-DD')
      })
      this.isDataUpdating = false
    },
    cbVersionId () {
      this.getKissScore()
    },
    filterParams: {
      handler (v) {
        this.keywordChips = Object.keys(v).reduce((arr, value) => {
          switch (value) {
            case 'keyword':
              if (v.keyword) {
                arr.push({
                  label: `keyword containing ${v.keyword}`,
                  value
                })
              }
              break
            case 'serp_features':
              if (v.serp_features.length) {
                const serpFeaturesOptions = DEFAULT_CONFIG.find(i => i.propName === 'serp_features').options
                arr.push({
                  label: `serp features equal to ${
                    (v.serp_features.length === 1 ? '' : 'one of: ') + v.serp_features.map(i => serpFeaturesOptions.find(item => item.value === i).label).join(', ')
                  }`,
                  value
                })
              }
              break
            default:
              if (v[value].operator && v[value].value) {
                const getOperatorLabel = (operator) => {
                  switch (operator) {
                    case 'equal':
                      return 'equal to'
                    case 'ne':
                      return 'not equal'
                    case 'gt':
                      return 'greater than'
                    case 'lt':
                      return 'less than'
                    case 'lte':
                      return 'less than/equal to'
                    case 'gte':
                      return 'greater than/equal to'
                  }
                }
                arr.push({
                  label: `${value === 'baseline_score' ? 'Baseline KISS' : 'Optimized KISS'} ${getOperatorLabel(v[value].operator)} ${v[value].value}`,
                  value
                })
              }
          }
          return arr
        }, [])
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    ...mapState('rankZoneCommon', {
      comparisonArray (state) {
        return !this.webPropertyGroupId ||
        (this.webPropertyGroupId.length &&
        (_.cloneDeep(state.webPropertyGroups).find(i => i._id === this.webPropertyGroupId) || {}).type === 'Clients') ? [] : [this.webPropertyGroupId]
      }
    }),
    ...mapState('briefContent', [
      'briefContent'
    ]),
    columnOptions () {
      let columnOptions = _.cloneDeep(DEFAULT_CONFIG)
      if (!this.isBaselineDisplay) {
        columnOptions = columnOptions.filter(i => !BASELINE_COLUMNS.includes(i.property))
      }
      return (this.isClientDifficultyScoreEnabled ? columnOptions : columnOptions.filter(i => i.propName !== 'diff_score')).map(i => ({ label: i.label.replace('<br/>', ' '), value: i.propName }))
    },
    availableDates () {
      let avaliableDates = this.briefContent.snapshots.map(date => moment(date).toDate())
      avaliableDates = _.sortBy(avaliableDates).reverse()
      return avaliableDates
    },
    sortedKeywords () {
      let filteredKeywords = _.cloneDeep(this.keywords)
      // filter serp features
      const filterBySerpFeature = (serpFeatures) => {
        if (this.filterParams.serp_features.length) {
          // if selected empty
          if (this.filterParams.serp_features.includes('empty') && !serpFeatures.length) {
            return true
          }
          // filter by selected serp features
          return serpFeatures.some((serp) => this.filterParams.serp_features.includes(serp))
        } else {
          return true
        }
      }

      const filterByScore = (score, type) => {
        if (typeof score !== 'number' || !this.filterParams[type].value || !this.filterParams[type].operator) {
          return true
        }
        switch (this.filterParams[type].operator) {
          case 'equal':
            return parseInt(score) === parseInt(this.filterParams[type].value)
          case 'ne':
            return parseInt(score) !== parseInt(this.filterParams[type].value)
          case 'gt':
            return parseInt(score) > parseInt(this.filterParams[type].value)
          case 'lt':
            return parseInt(score) < parseInt(this.filterParams[type].value)
          case 'lte':
            return parseInt(score) <= parseInt(this.filterParams[type].value)
          case 'gte':
            return parseInt(score) >= parseInt(this.filterParams[type].value)
        }
      }

      filteredKeywords = filteredKeywords.filter(item => {
        const keywordFilter = !this.filterParams.keyword || item.keyword.toLowerCase().includes(this.filterParams.keyword.toLowerCase())
        return keywordFilter &&
          filterBySerpFeature(item.serp_features) &&
          filterByScore(item?.baseline_score, 'baseline_score') &&
          filterByScore(item?.optimized_score, 'optimized_score')
      })
      if (!this.sortConfig.length) {
        return filteredKeywords
      }
      return helper.sortByMultipleParams(filteredKeywords, this.sortConfig)
    },
    dataMissing () {
      return !this.sortedKeywords.length && !this.isDataUpdating
    },
    /**
     * table config
     * @see BaseSortMixins
     */
    keywordsTableColumnsConfig () {
      let keywordsTableColumnsConfig = _.cloneDeep(DEFAULT_CONFIG)
      if (!this.isBaselineDisplay) {
        keywordsTableColumnsConfig = keywordsTableColumnsConfig.filter(i => !BASELINE_COLUMNS.includes(i.property))
      }
      return (this.isClientDifficultyScoreEnabled ? keywordsTableColumnsConfig : keywordsTableColumnsConfig.filter(i => i.propName !== 'diff_score')).filter(i => this.enabledColumns.includes(i.propName))
    },
    summaryHeaderData () {
      let baselineScore = 0
      let optimizedScore = 0
      let change = 0
      let msv = 0
      let score = 0
      let diffScore = 0
      this.sortedKeywords.forEach(keyword => {
        baselineScore += keyword.baseline_score > -1 ? keyword.baseline_score : 0 // coalesce NDs to 0
        optimizedScore += keyword.optimized_score > -1 ? keyword.optimized_score : 0 // coalesce NDs to 0
        change += keyword.change > -1 ? keyword.change : 0 // coalesce NDs to 0
        msv += parseInt(keyword.monthly_search_volume.replaceAll(',', ''))
        score += keyword.score
        diffScore += keyword.diff_score
      })
      const keyword = this.sortedKeywords.length

      const getAverage = (value, kw = keyword) => {
        let result = (value / kw)
        if (!result) {
          return '0'
        }
        result = result.toFixed(2).toString()
        if (result.endsWith('.00')) {
          result = result.substring(0, result.length - 3)
        }
        return result
      }
      return {
        keyword,
        baseline_score: getAverage(baselineScore, this.sortedKeywords.filter(kw => kw.baseline_score > -1).length),
        optimized_score: getAverage(optimizedScore, this.sortedKeywords.filter(kw => kw.optimized_score > -1).length),
        change: getAverage(change, this.sortedKeywords.filter(kw => kw.change > -1).length),
        monthly_search_volume: filters.commaDividedNumber(msv),
        score: getAverage(score),
        diff_score: getAverage(diffScore)
      }
    },
    modalProps: function () {
      const shareModalProps = {
        width: '75%'
      }
      return { ...baseModalProps, ...shareModalProps }
    }
  },
  filters: {
    commaDividedNumber: filters.commaDividedNumber,
    absValue: filters.absValue
  },
  methods: {
    ...mapActions('briefContent', ['getKeywordsSnapshot', 'getKeywordsBaselineSnapshot']),
    applyFilter(value, filterName) {
      return this.$options.filters[filterName](value)
    },
    onRelScoreChanged (keyword) {
      this.$emit('update-score', keyword)
    },
    /**
     * callback of sort buttons
     * */
    onHeaderClicked (column, direction) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = column.propName
        this.sortConfig[0].sortDirection = direction
        this.sortConfig[0].sortByLenght = column.sortByLenght
      } else {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: column.propName,
          sortByLenght: column.sortByLenght
        }]
      }
    },
    /**
     * update filterParam
     * filter logic triggers from filterColumn watcher {@link filterParamsWatcher}
     * @param event data from BaseMultiSelectSortableHeader
     * @param column - column config
     */
    filterColumn (event, column) {
      if (column.isSerp) {
        this.filterParams.serp_features = []
        this.filterParams.serp_features = _.cloneDeep(event)
      } else if (column.propName === 'keyword') {
        this.filterParams.keyword = event
      } else {
        this.filterParams[column.propName] = _.cloneDeep(event)
      }
    },
    removeFilter (key) {
      const removeFilter = (ref) => {
        const child = this.$refs[ref][0].getChild()
        child.cancel()
        child.apply()
      }
      if (key === 'clearAll') {
        this.keywordChips.forEach(i => {
          removeFilter(i.value)
        })
      } else {
        removeFilter(key)
      }
    },
    getChangeValue (v) {
      if (typeof v === 'string' && v.startsWith('+')) {
        return v
      } else {
        return filters.absValue(v)
      }
    },
    getSummary (keyword, propName) {
      return propName === 'optimized_score' ? keyword.summary : keyword.baseline_summary
    },
    updateKeywordDiff (count, keyword) {
      this.$emit('update-score', { ...(_.cloneDeep(keyword)), diff_score: count, isDiffScoreUpdate: true })
    },
    handleKeywordsAdded() {
      this.updateKeywords()
    },
    async updateKeywords(date = this.briefContent.selectedDate) {
      const params = {
          clientId: this.clientId,
          briefId: this.briefContent._id,
          web_property_group_id: this.webPropertyGroupId
        }

        const dateObj = moment(date)
        const isDateValid = dateObj.isValid()
        if (isDateValid) {
          params.snapshotDate = dateObj.format('YYYY-MM-DD')
        }
        this.isDataUpdating = true
        if (!this.isBaselineDisplay && isDateValid) {
          await this.getKeywordsSnapshot(params)
        } else if (isDateValid) {
          await this.getKeywordsBaselineSnapshot({
            ...params,
            cbVersionId: this.cbVersionId,
            optimizedContent: this.optimizedContent
          })
        }
        this.isDataUpdating = false
    },
    async getKissScore () {
      // if (this.optimizedContent) {
      //   const div = document.createElement('div')
      //   div.innerHTML = this.optimizedContent
      //   const h1 = div.querySelector('h1')
      //   if (!h1 || !h1.innerHTML) {
      //     ToasterService.showToast(ToasterService.INFO_TYPE, 'Optimized must include "Heading 1" format')
      //     return
      //   }
      // }

      this.isDataUpdating = true
      this.getKeywordsBaselineSnapshot({
        clientId: this.clientId,
        briefId: this.briefContent._id,
        snapshotDate: moment(this.briefContent.selectedDate).format('YYYY-MM-DD'),
        cbVersionId: this.cbVersionId,
        optimizedContent: this.optimizedContent
      })
      this.isDataUpdating = false
    },
    exportCSV () {
      if (!this.isKeywordsPresent) {
        return
      }
      const handler = (columns) => {
        return item => {
          const row = []
          columns.forEach(name => {
            let route = null
            if (['tag_title_current', 'tag_meta_description_current', 'tag_h1_current', 'tag_title_recommended', 'tag_meta_description_recommended', 'tag_h1_recommended'].includes(name)) {
              route = this.briefContent
            } else if (name === 'mapping_match') {
              row.push(item.ranking_url && item.mapped_url && item.ranking_url === item.mapped_url ? '"Match"' : '"Mismatch"')
              return
            } else if (['news', 'news_pack_results', 'video', 'local_3_pack', 'answer_box', 'people_also_ask'].includes(name)) {
              row.push(item.serp_features.includes(name) ? 'True' : 'False')
              return
            } else if (name === 'diff_score') {
              row.push(`"${item.diff_score || 0}"`)
              return
            } else {
              route = item
            }

            if ((typeof route[name] === 'string' && route[name].trim()) ||
              (route[name] && typeof route[name] !== 'string') ||
              this.isBaselineDisplay) {
              row.push(`"${route[name]}"`)
            } else {
              row.push('"NR"')
            }
          })
          data.push(row)
        }
      }
      let header = ['"Rank Date"', '"Keyword"', '"Relevancy Score"', '"Monthly Searches"', '"Rank Zone"', '"Ranking URL"',
        '"Mapped URL"', '"Rank:Mapping Match"', '"tag_title_current"', '"tag_meta_description_current"',
        '"tag_h1_current"', '"tag_title_recommended"', '"tag_meta_description_recommended"', '"tag_h1_recommended"',
        'Answer Box',
        'Brand Pack',
        'Image Pack',
        'Knowledge Graph',
        'Local 3 Pack',
        'News Pack',
        'People Also Ask',
        'Related Searches',
        'Shopping Box',
        'Video Pack',
        'Ad Count',
        'Geo',
        'Locale',
        'Total Results']
      const data = []
      let columnNames = ['date', 'keyword', 'score', 'monthly_search_volume', 'rank_zone', 'ranking_url', 'mapped_url',
        'mapping_match', 'tag_title_current', 'tag_meta_description_current', 'tag_h1_current',
        'tag_title_recommended', 'tag_meta_description_recommended', 'tag_h1_recommended',
        'answer_box',
        'brand_pack',
        'image_pack',
        'knowledge_graph',
        'local_3_pack',
        'news_pack',
        'people_also_ask',
        'related_searches',
        'shopping_box',
        'video_pack',
        'ad_count',
        'geo',
        'locale',
        'total_number_of_results']
      if (this.isBaselineDisplay) {
        header = ['"Rank Date"', '"Keyword"', '"Relevancy Score"', '"Monthly Searches"', '"KISS Baseline Score"', '"KISS Optimized Score"',
          '"KISS Change"', '"Rank Zone"', '"Ranking URL"', '"Mapped URL"', '"Rank:Mapping Match"', '"tag_title_current"',
          'Answer Box',
          'Brand Pack',
          'Image Pack',
          'Knowledge Graph',
          'Local 3 Pack',
          'News Pack',
          'People Also Ask',
          'Related Searches',
          'Shopping Box',
          'Video Pack',
          'Ad Count',
          'Geo',
          'Locale',
          'Total Results']
        columnNames = ['date', 'keyword', 'score', 'monthly_search_volume', 'baseline_score', 'optimized_score',
          'change', 'rank_zone', 'ranking_url', 'mapped_url', 'mapping_match', 'tag_title_current',
          'answer_box',
          'brand_pack',
          'image_pack',
          'knowledge_graph',
          'local_3_pack',
          'news_pack',
          'people_also_ask',
          'related_searches',
          'shopping_box',
          'video_pack',
          'ad_count',
          'geo',
          'locale',
          'total_number_of_results']
      }
      if (this.isClientDifficultyScoreEnabled) {
        const index = columnNames.indexOf('score') + 1
        header.splice(index, 0, '"Diff Score"')
        columnNames.splice(index, 0, 'diff_score')
      }
      data.push(header)
      this.briefContent.keywords.forEach(handler(columnNames))
      const name = `${this.selectedClient.name.replace(/ /g, '_')}_${this.isBaselineDisplay ? `KISS-Report_${this.briefContent.name}` : 'Keywords'}_${moment().format('YYYY-MM-DD')}`
      csvExport(name, data)
    },
    mappedRankingUrl (url) {
      if (!url) return url

      const clearedUrl = this.clearedURL(url)
      let mappedRankingUrl = clearedUrl.replace(/\//g, ' > ')
      // remove '>' if link looks like 'url.com > route >'
      if (mappedRankingUrl.charAt(mappedRankingUrl.length - 1) === '>') {
        mappedRankingUrl.slice(0, -1)
      }
      return mappedRankingUrl
    },
    clearedURL (url) {
      if (!url) return url

      const prefixes = ['http://www.', 'https://www.', 'https://', 'http://']
      prefixes.forEach(item => {
        if (url.startsWith(item)) {
          url = url.split(item)[1]
        }
      })
      return url
    },
    /**
     * Opens BaseKeywordMovementHistoryModal
     * @param keyword
     */
    openModal (keyword) {
      this.selectedKeyword = keyword
      this.$modal.show('history')
    },
    closeModal (isUrlChanged) {
      this.selectedKeyword = ''
      this.$modal.hide('history')
      isUrlChanged && this.getKeywordsSnapshot({
        clientId: this.clientId,
        briefId: this.briefContent._id,
        web_property_group_id: this.webPropertyGroupId,
        snapshotDate: moment(this.briefContent.selectedDate).format('YYYY-MM-DD')
      })
    },
    checkUrlsMismatch(keyword) {
      return this.briefContent.published_url !== keyword.ranking_url
    }
  }
}

</script>

<style scoped lang="scss">
  .keyword-block-chip {
    border-radius: 15px;
    background-color: $rfx-light-blue;
    height: 30px;
    margin-right: 16px;
    overflow: hidden;
    color: white;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 3px 0 3px 12px;

    &-filter {
      .block-chip__name {
        max-width: 270px;
      }
    }

    &_clear-all {
      background-color: $rfx-medium-march;
      font-weight: 500;
      padding: 0 10px;
      cursor: pointer;
    }

    &__name {
      font-size: 12px;
      max-width: 270px;
      color: #fff;
      text-transform: lowercase;
      width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__button {
      position: relative;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      pointer-events: auto;

      &:before,
      &:after {
        position: absolute;
        right: 12px;
        top: 9px;
        content: " ";
        height: 12px;
        width: 2px;
        background-color: #fff;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }

      &:hover {
        &:before, &:after {
          background-color: $rfx-silver;
        }
      }
    }
  }

  .keywords__not-found {
    font-size: 27px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 60px;
    color: $rfx-medium-grey;
  }

  .keywords__column-selector {
    margin-right: 24px;
  }

  .keyword-base-table-collapsible {
    &::v-deep .collapsible__content {
      overflow: visible;
    }
  }

  .rescore-optimized {
    color: $rfx-dark-march;
    background-color: transparent;
    border: none;
    outline: none;
    line-height: 24px;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 1.25px;
    display: flex;
    align-items: center;
    margin-left: auto;
    margin-right: 18px;
    text-transform: uppercase;

    span {
      font-size: 21px;
      padding-right: 8px;
    }
  }

  .keywords {

    &__ranking_url,
    &__keyword {
      flex: 0.4 0 160px;
      color: $rfx-dark-march;
      font-weight: 500;
    }

    &__keyword {

      &::v-deep .multi-select__title-container {
        padding-left: 0;
        padding-right: 0;
      }
    }

    &__baseline_score,
    &__optimized_score {
      flex: 0 0 130px;
      padding-right: 0;

      &::v-deep .multi-select__title-container {
        padding-left: 22px;
        padding-right: 0;
      }

      &.common-table__cell {
        flex: 0 0 110px;
      }

      &.common-table__cell {
        padding: 0;
      }
    }

    &__ranking_url {

      &__keyword {
        span {
          &:hover {
            color: $rfx-bright-blue !important;
            text-decoration: underline;
            cursor: pointer;
          }
        }

        &_keyword {
          padding-left: 29px;
        }
      }

      a {
        display: inline-block;
        width: 100%;
        text-decoration: none;

        &.url-mismatch div {
          color: $rfx-orange
        }

        &:hover {
          color: $rfx-bright-blue !important;
          text-decoration: underline;
        }

        /*styles for safari*/
        div {
          display: inline;
          color: $rfx-dark-march;

          &:hover {
            color: $rfx-bright-blue !important;
            text-decoration: underline;
          }
        }

        &:visited {
          color: $rfx-dark-march;
        }
      }
    }

    &__select {
      width: 80px;
    }

    &__serp_features {
      flex: 0 0 155px;
      color: $rfx-dark-march;
      font-weight: 500;

      &::v-deep .multi-select__title-container {
        padding-left: 20px;
      }
    }

    &__rank_zone,
    &__diff_score,
    &__score {
      flex: 0 0 95px;
      color: $rfx-light-march;
    }

    &__diff_score {

      &.common-table__cell {
        width: 95px !important;
      }

      .page-title__content {
        position: relative;
        left: -5px;
        margin: 0 !important;
        width: -webkit-fill-available;
      }

      &::v-deep input {
        color: $rfx-light-march;
        font-size: 13px;
        height: 14px !important;
        font-weight: 400;
      }

      &::v-deep .page-title__action-buttons {
        right: 0 !important;
        bottom: -134% !important;
      }
    }

    &__change {
      padding-top: 14px;
      padding-bottom: 14px;

      &.common-table__cell {
        flex: 0 0 80px;
        padding: 22px 8px 22px 22px;
      }
    }

    &__change,
    &__monthly_search_volume {
      flex: 0 0 70px;
      color: $rfx-light-march;

      &.common-table__cell {
        padding-left: 22px;
      }
    }

    &__monthly_search_volume {

      &.common-table__cell {
        flex: 0 0 68px;
      }
    }

    &__diff_score,
    &__score {
      padding: 0 20px;
      display: flex;
      align-items: center;
    }

    &__clickable-cell {
      cursor: pointer;
    }
  }

  .common-table {
    overflow: visible;

    &__header {
      background-color: rgba(1, 22, 39, 0.1);
    }

    &__cell {

      &.keywords__serp_features {
        flex-basis: 115px;
        padding-top: 15px;
        padding-bottom: 15px;
      }

      &:first-child {
        padding-right: 0;
      }

      &:last-child {
        padding-right: 55px;
      }
    }

    &__summary {

      .keywords__diff_score,
      .keywords__score {
        padding: 0 20px;
      }

      .keywords__optimized_score,
      .keywords__baseline_score {
        padding-left: 22px;
        flex: 0 0 108px;
      }

      .keywords__serp_features {
        flex: 0 0 135px;
        padding-left: 20px;
      }

      .keywords__monthly_search_volume,
      .keywords__change {
        flex: 0 0 70px;
        padding: 0 20px;
      }

      .keywords__rank_zone {
        flex: 0 0 95px;
        padding: 0 20px;
      }

      .keywords__ranking_url {
        padding-left: 20px;
      }
    }

    &__average_cell {
      display: flex;
      align-items: center;
      font-weight: bold;
      color: $rfx-dark-march;
      padding-top: 14px !important;
      padding-bottom: 14px !important;
      height: 16px !important;

      &:first-child {
        padding-left: 30px;

        &.keywords__optimized_score,
        &.keywords__baseline_score {
          flex: 0 0 100px;
        }
      }

      &:last-child {
        &.keywords__rank_zone {
          padding-right: 30px;
        }

        &.keywords__ranking_url {
          padding-right: 60px;
        }
      }
    }

    &__header-cell {
      color: $rfx-dark-march;
      font-weight: bold;
      font-size: 14px;

      &_zero-padding {
        padding: 0;

        &:first-child {
          //max-width: 37.6%;

          &::v-deep .multi-select__title-container{
            padding-left: 30px;
          }
        }
      }

      &__wrapper {
        height: 100%;
        cursor: pointer;
      }
    }
  }

  .no-data-block {
    min-height: 300px;
  }
</style>
